import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { getMembershipGeneral } from "../api/PayApi";
import {
  useCrossTabAuthSync,
  readAuthMeta,
  clearAuthMeta,
  saveAuthMeta,
} from "./AuthSync";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [membershipState, setMembershipState] = useState(null); // { email, data }
  const membershipRef = useRef(membershipState);
  useEffect(() => {
    membershipRef.current = membershipState;
  }, [membershipState]);

  const loadUserFromToken = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const payload = jwtDecode(token);
      const isExpired = payload.exp * 1000 < Date.now();
      if (isExpired) {
        setUser(null);
        return;
      }

      const u = {
        id: payload.id,
        email: payload.sub,
        displayName: payload.displayName,
        mailNickname: payload.mailNickname,
        provider: payload.provider,
        emailVerified: payload.emailVerified,
        phone: payload.phone,
        address: payload.address,
        secondaryEmail: payload.secondaryEmail,
        role: payload.rol || payload.role,
      };

      setUser(u);

      const meta = readAuthMeta();
      if (!meta?.email || meta.email !== u.email) {
        saveAuthMeta({ email: u.email });
      }
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    loadUserFromToken();
  }, []);

  useCrossTabAuthSync(() => {
    loadUserFromToken();
  });

  const login = (token) => {
    localStorage.setItem("token", token);
    loadUserFromToken();
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    clearAuthMeta();
    // clear membership for this user on logout
    try {
      if (membershipState?.email) {
        localStorage.removeItem(`membership_${membershipState.email}`);
      }
    } catch (e) {}
    setMembershipState(null);
  };

  // --- Membership helpers ---
  const _loadMembershipFromStorage = (email) => {
    if (!email) return null;
    try {
      const raw = localStorage.getItem(`membership_${email}`);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const _saveMembershipToStorage = (email, data) => {
    if (!email) return;
    try {
      localStorage.setItem(`membership_${email}`, JSON.stringify(data));
    } catch (e) {}
  };

  // Deduplicate in-flight membership requests per email
  const inflightRef = useRef(new Map());

  const ensureMembership = useCallback(async (email) => {
    if (!email) return null;
    // In-memory cache
    const mem = membershipRef.current;
    if (mem && mem.email === email) {
      return mem.data;
    }

    // LocalStorage cache
    const stored = _loadMembershipFromStorage(email);
    if (stored) {
      setMembershipState({ email, data: stored });
      return stored;
    }

    // In-flight deduplication
    const inflight = inflightRef.current;
    if (inflight.has(email)) {
      try {
        return await inflight.get(email);
      } catch (e) {
        return null;
      }
    }

    // Fetch from API
    const promise = (async () => {
      const res = await getMembershipGeneral(email);
      
      const status = String(res?.status || '').toLowerCase();
      const isValidResponse = res && 
                             status === 'active';
            
      // Solo guardar en memoria y localStorage si la respuesta es válida
      if (isValidResponse) {
        setMembershipState({ email, data: res });
        _saveMembershipToStorage(email, res);
      } else {
        // No guardar nada si no pasa la validación
      }
      
      return res;
    })();

    inflight.set(email, promise);
    try {
      return await promise;
    } catch (err) {
      return null;
    } finally {
      inflight.delete(email);
    }
  }, []);

  // Force-refresh membership from API once (deduped while in-flight)
  const refreshMembership = useCallback(async (email) => {
    if (!email) return null;
    const inflight = inflightRef.current;
    if (inflight.has(email)) {
      try {
        return await inflight.get(email);
      } catch (e) {
        return null;
      }
    }
    const promise = (async () => {
      const res = await getMembershipGeneral(email);
      
      const status = String(res?.status || '').toLowerCase();
      const isValidResponse = res && 
                             status === 'active';
      
      // Solo guardar en memoria y localStorage si la respuesta es válida
      if (isValidResponse) {
        setMembershipState({ email, data: res });
        _saveMembershipToStorage(email, res);
      }
      
      return res;
    })();
    inflight.set(email, promise);
    try {
      return await promise;
    } catch (e) {
      return null;
    } finally {
      inflight.delete(email);
    }
  }, []);

  // Cargar membresía automáticamente cuando cambia el usuario
  useEffect(() => {    
    if (user?.email) {
      ensureMembership(user.email)
    } else {
      setMembershipState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        authenticated: !!user,
        login,
        logout,
        membership: membershipState?.data || null,
        ensureMembership,
  refreshMembership,
        // expose a setter in case other parts need to update it
        setMembership: (data) => {
          if (user?.email) {
            setMembershipState({ email: user.email, data });
            _saveMembershipToStorage(user.email, data);
          } else {
            setMembershipState({ email: null, data });
          }
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
